import { ErrorMessages } from "@/shared/common/enums";
import type { ValidationSchemaProps, } from "@/shared/common/types";
import {
  getAccountNumberValidationSchema,
  getBeneficiaryNameValidationSchema,
  getCardNumberValidationSchema,
} from "@/shared/validations";
import * as Yup from "yup";

export interface AttachmentSchemaProps extends ValidationSchemaProps {
  existingNumbers?: string[];
  isDuplicate?: boolean;
  hasSameClientCode?: boolean;
  hasDifferentClient?: boolean;
}

export const accountAttachmentValidationSchema = ({
  t, existingNumbers = [], isDuplicate, hasSameClientCode, hasDifferentClient,
}: AttachmentSchemaProps) => {
  return Yup.object().shape({
    number: getAccountNumberValidationSchema({ t })
      .test("api-duplicate", t(ErrorMessages.DuplicateBeneficiaryNumber), () => !isDuplicate)
      .test(
        "api-same-client-code",
        t(ErrorMessages.DuplicateBeneficiaryClientCode),
        () => !hasSameClientCode
      )
      .test("api-different-client", t(ErrorMessages.DifferentClient), () => !hasDifferentClient)
      .test("existing", t(ErrorMessages.DuplicateBeneficiaryNumber), (value) =>
        !existingNumbers.includes(value ?? ""),
      ),
    note: Yup.string(),
  });
};

export const cardAttachmentValidationSchema = ({
  t, existingNumbers = [], isDuplicate, hasSameClientCode, hasDifferentClient,
}: AttachmentSchemaProps) => {
  return Yup.object().shape({
    number: getCardNumberValidationSchema({ t })
      .test("api-duplicate", t(ErrorMessages.DuplicateBeneficiaryNumber), () => !isDuplicate)
      .test(
        "api-same-client-code",
        t(ErrorMessages.DuplicateBeneficiaryClientCode),
        () => !hasSameClientCode
      )
      .test("api-different-client", t(ErrorMessages.DifferentClient), () => !hasDifferentClient)
      .test("existing", t(ErrorMessages.DuplicateBeneficiaryNumber), (value) =>
        !existingNumbers.includes(value ?? ""),
      ),
    note: Yup.string().nullable(),
  });
};

export const renameBeneficiaryValidationSchema = ({ t }: ValidationSchemaProps) =>
  Yup.object().shape({
    name: getBeneficiaryNameValidationSchema({ t }),
  });

export const createBeneficiaryValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.object().shape(
    {
      name: getBeneficiaryNameValidationSchema({ t }),
      isAvailableForOrgUsers: Yup.boolean(),
      accounts: Yup.array()
        .of(accountAttachmentValidationSchema({ t }).nullable())
        .nullable()
        .when("cards", {
          is: (cards: unknown[]) => !cards?.length,
          then: (schema) => schema.min(1, t(ErrorMessages.BeneficiaryAtLeastOne)),
        }),
      cards: Yup.array()
        .of(cardAttachmentValidationSchema({ t }).nullable())
        .nullable()
        .when("accounts", {
          is: (accounts: unknown[]) => !accounts?.length,
          then: (schema) => schema.min(1, t(ErrorMessages.BeneficiaryAtLeastOne)),
        }),
    },
    [["accounts", "cards"]],
  );
};

